import type { ReactNode } from 'react';
import {
  ContractSubTabsNav,
  CONTRACT_SUB_TABS,
  type ContractSubTabSlug,
} from './contract-sub-tabs';

/**
 * ContractHeader (C20-133) — the shared header block at the top of every
 * Contract Compliance sub-route. Ports the prototype's `contractHeader()`
 * helper (`screens-modules-tabs.js` lines 52-64) into a React component.
 *
 * Visual contract (prototype):
 *   <Module title> · <Sub-tab name>     [<header actions>]
 *   <meta line — SOW state · CO count · deliverables tracked>
 *   <horizontal sub-tabs strip>
 *
 * The meta line ("SOW v2 — Apr 18 · 1 active change order + 1 draft · 30
 * deliverables tracked") is *not* hardcoded — every sub-route page passes
 * its own meta string derived from real backend data (or "Pending" copy
 * when a backend is missing). The component never invents copy.
 */

export interface ContractHeaderProps {
  engagementId: string;
  /** Which sub-tab is currently active — sub-tabs strip highlights this. */
  active: ContractSubTabSlug;
  /** Free-form meta line beneath the title — pass real data, never invent. */
  meta?: ReactNode;
  /** Action buttons rendered in the top-right slot. */
  actions?: ReactNode;
}

const TAB_LABEL: Record<ContractSubTabSlug, string> = Object.fromEntries(
  CONTRACT_SUB_TABS.map((t) => [t.slug, t.label]),
) as Record<ContractSubTabSlug, string>;

export function ContractHeader({ engagementId, active, meta, actions }: ContractHeaderProps) {
  const subLabel = TAB_LABEL[active];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-h1 font-semibold text-ink-900">
            Contract Compliance · <span className="font-medium text-ink-500">{subLabel}</span>
          </h1>
          {meta ? <div className="text-meta text-ink-500">{meta}</div> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      <ContractSubTabsNav engagementId={engagementId} active={active} />
    </div>
  );
}
